// SH-1409 — server-side persistence of the selected calendar view.
//
// Reads the view the user last chose from the already-loaded general
// preferences (the app loads them once at the root via
// `useLoadGeneralPreferencesOnMount`, so this only selects — it never triggers
// its own fetch), and exposes a `persistView` that upserts the choice. Mirrors
// the map's `CollisionToggle` preference pattern.

import { useCallback, useEffect, useMemo } from 'react'
import { debounce } from 'lodash'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import {
  IUserPreferenceGeneralListItemType,
  saveGeneralPreferences,
  userGeneralPreferencesList,
} from '@user/store/userPreferences/userPreferencesGeneral'
import type { CalendarView } from '../typings'
import { getPreferredCalendarView } from '../viewPreference'
import { withGeneralPreferencesLock } from './generalPreferencesLock'

// Rapid view switching (Month→Week→Day) shouldn't fire one save per click.
// Debounce so only the settled selection reaches POST /UserPreferences/general;
// this also collapses the rapid-toggle out-of-order-save window into a single
// trailing write. Matches the shared `useDebounce` default.
const SAVE_DEBOUNCE_MS = 500

export type UseCalendarViewPreferenceResult = {
  /** The persisted view to restore, or `undefined` to use the default. */
  preferredView: CalendarView | undefined
  /** Persist the newly selected view (debounced, fire-and-forget, upsert-by-Type). */
  persistView: (view: CalendarView) => void
}

export function useCalendarViewPreference(): UseCalendarViewPreferenceResult {
  const dispatch = useAppDispatch()
  const userGeneralPreferences = useSelector(userGeneralPreferencesList)
  const preferredView = getPreferredCalendarView(userGeneralPreferences)

  const debouncedSave = useMemo(
    () =>
      debounce((view: CalendarView) => {
        // Not awaited, and the slice has no `rejected` case, so a failed save
        // (network/500) leaves the store — and therefore the view — untouched
        // (EC-3). The next successful toggle re-persists (last-write-wins).
        // Routed through the shared lock so this write can never overlap
        // in-flight with useCalendarFilterPreferences's write to the same
        // GeneralPreference resource (Copilot #1).
        withGeneralPreferencesLock(() =>
          dispatch(
            saveGeneralPreferences({
              type: IUserPreferenceGeneralListItemType.UnifiedCalendarView,
              value: view,
            }),
          ).unwrap(),
        ).catch(() => {
          // Same fire-and-forget contract as before the lock was added.
        })
      }, SAVE_DEBOUNCE_MS),
    [dispatch],
  )

  // Flush a pending selection on unmount so a view chosen right before
  // navigating away is still committed. A hard refresh, tab close, or external
  // navigation never unmounts the tree, so also flush on `pagehide` — otherwise
  // a view picked inside the debounce window is lost on the next load.
  useEffect(() => {
    const flush = () => debouncedSave.flush()
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [debouncedSave])

  const persistView = useCallback(
    (view: CalendarView) => debouncedSave(view),
    [debouncedSave],
  )

  return { preferredView, persistView }
}
